import { collection, doc, getDocs, addDoc, updateDoc, deleteDoc, query, where, orderBy, increment } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

/**
 * ดึงข้อมูล tier configurations ทั้งหมด
 * @returns {Promise<Object>} tier configurations แยกตาม role
 */
export const fetchTierConfigurations = async () => {
  try {
    const configsRef = collection(db, 'tierConfigurations');
    const snapshot = await getDocs(configsRef);
    
    const configs = {};
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      if (!configs[data.role]) configs[data.role] = {};
      configs[data.role][data.tierKey] = { id: doc.id, ...data };
    });
    
    return configs;
  } catch (error) {
    console.error('Error fetching tier configurations:', error);
    throw error;
  }
};

/**
 * ดึงข้อมูล tier configuration เฉพาะ role และ tier
 * @param {string} role - role ของผู้ใช้
 * @param {string} tierKey - key ของ tier
 * @returns {Promise<Object|null>} tier configuration
 */
export const fetchTierConfiguration = async (role, tierKey) => {
  try {
    const configsRef = collection(db, 'tierConfigurations');
    const q = query(configsRef, where('role', '==', role), where('tierKey', '==', tierKey));
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) return null;
    
    const doc = snapshot.docs[0];
    return { id: doc.id, ...doc.data() };
  } catch (error) {
    console.error('Error fetching tier configuration:', error);
    throw error;
  }
};

/**
 * บันทึก tier configuration ใหม่
 * @param {Object} tierConfig - ข้อมูล tier configuration
 * @returns {Promise<string>} ID ของ document ที่สร้างใหม่
 */
export const createTierConfiguration = async (tierConfig) => {
  try {
    const configsRef = collection(db, 'tierConfigurations');
    const docRef = await addDoc(configsRef, {
      ...tierConfig,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating tier configuration:', error);
    throw error;
  }
};

/**
 * อัพเดท tier configuration
 * @param {string} configId - ID ของ tier configuration
 * @param {Object} updates - ข้อมูลที่ต้องการอัพเดท
 * @returns {Promise<void>}
 */
export const updateTierConfiguration = async (configId, updates) => {
  try {
    const configRef = doc(db, 'tierConfigurations', configId);
    await updateDoc(configRef, {
      ...updates,
      updatedAt: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error updating tier configuration:', error);
    throw error;
  }
};

/**
 * ลบ tier configuration
 * @param {string} configId - ID ของ tier configuration
 * @returns {Promise<void>}
 */
export const deleteTierConfiguration = async (configId) => {
  try {
    const configRef = doc(db, 'tierConfigurations', configId);
    await deleteDoc(configRef);
  } catch (error) {
    console.error('Error deleting tier configuration:', error);
    throw error;
  }
};

/**
 * ดึงข้อมูล promotions ทั้งหมด
 * @returns {Promise<Array>} รายการ promotions
 */
export const fetchPromotions = async () => {
  try {
    const promotionsRef = collection(db, 'promotions');
    const q = query(promotionsRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching promotions:', error);
    throw error;
  }
};

/**
 * สร้าง promotion ใหม่
 * @param {Object} promotion - ข้อมูล promotion
 * @returns {Promise<string>} ID ของ document ที่สร้างใหม่
 */
export const createPromotion = async (promotion) => {
  try {
    const promotionsRef = collection(db, 'promotions');
    const docRef = await addDoc(promotionsRef, {
      ...promotion,
      currentUses: 0, // เพิ่มการติดตามการใช้งาน
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating promotion:', error);
    throw error;
  }
};

/**
 * อัพเดท promotion
 * @param {string} promotionId - ID ของ promotion
 * @param {Object} updates - ข้อมูลที่ต้องการอัพเดท
 * @returns {Promise<void>}
 */
export const updatePromotion = async (promotionId, updates) => {
  try {
    const promotionRef = doc(db, 'promotions', promotionId);
    await updateDoc(promotionRef, {
      ...updates,
      updatedAt: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error updating promotion:', error);
    throw error;
  }
};

/**
 * ลบ promotion
 * @param {string} promotionId - ID ของ promotion
 * @returns {Promise<void>}
 */
export const deletePromotion = async (promotionId) => {
  try {
    const promotionRef = doc(db, 'promotions', promotionId);
    await deleteDoc(promotionRef);
  } catch (error) {
    console.error('Error deleting promotion:', error);
    throw error;
  }
};

/**
 * ตรวจสอบว่า promotion กำลังใช้งานอยู่หรือไม่
 * @param {Object} promotion - ข้อมูล promotion
 * @returns {boolean} true ถ้า promotion กำลังใช้งาน
 */
export const isPromotionActive = (promotion) => {
  if (!promotion.isActive) return false;
  
  const now = new Date();
  const startDate = new Date(promotion.startDate);
  const endDate = new Date(promotion.endDate);
  
  return now >= startDate && now <= endDate;
};

/**
 * คำนวณราคาหลังส่วนลด
 * @param {number} originalPrice - ราคาเดิม
 * @param {Object} promotion - ข้อมูล promotion
 * @returns {number} ราคาหลังส่วนลด
 */
export const calculateDiscountedPrice = (originalPrice, promotion) => {
  if (!promotion || !isPromotionActive(promotion)) {
    return originalPrice;
  }
  
  if (promotion.discountType === 'percentage') {
    return Math.max(0, originalPrice * (1 - promotion.discountValue / 100));
  } else if (promotion.discountType === 'fixed') {
    return Math.max(0, originalPrice - promotion.discountValue);
  }
  
  return originalPrice;
};

/**
 * ดึงข้อมูล promotions ที่ใช้งานได้สำหรับ role และ tier ที่กำหนด
 * @param {string} role - role ของผู้ใช้
 * @param {string} tier - tier ของผู้ใช้
 * @returns {Promise<Array>} รายการ promotions ที่ใช้งานได้
 */
export const getActivePromotions = async (role, tier) => {
  try {
    const promotionsRef = collection(db, 'promotions');
    const q = query(
      promotionsRef, 
      where('isActive', '==', true),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    
    const promotions = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // กรอง promotions ที่ตรงกับ role และ tier
    return promotions.filter(promotion => {
      const roleMatch = promotion.targetRole === 'all' || promotion.targetRole === role;
      const tierMatch = promotion.targetTier === 'all' || promotion.targetTier === tier;
      
      return roleMatch && tierMatch && isPromotionActive(promotion);
    });
  } catch (error) {
    console.error('Error getting active promotions:', error);
    throw error;
  }
};

/**
 * สร้าง default tier configurations
 * @returns {Promise<void>}
 */
export const initializeDefaultTierConfigurations = async () => {
  try {
    const defaultConfigs = {
      customer: {
        free: { 
          name: 'Free', 
          price: 0, 
          features: ['ดูรูป 1 รูป/วัน'], 
          color: 'bg-gray-100 text-gray-800',
          maxImages: 0,
          dailyViews: 1,
          monthlyBookings: 0,
          revenueShare: 0,
          isActive: true,
          trialDays: 0
        },
        basic: { 
          name: 'Basic', 
          price: 100, 
          features: ['ดูรูป 5 รูป/วัน', 'จองได้ 3 ครั้ง/เดือน'], 
          color: 'bg-blue-100 text-blue-800',
          maxImages: 0,
          dailyViews: 5,
          monthlyBookings: 3,
          revenueShare: 0,
          isActive: true,
          trialDays: 7
        },
        silver: { 
          name: 'Silver', 
          price: 300, 
          features: ['ดูรูป 15 รูป/วัน', 'จองได้ 10 ครั้ง/เดือน'], 
          color: 'bg-gray-100 text-gray-800',
          maxImages: 0,
          dailyViews: 15,
          monthlyBookings: 10,
          revenueShare: 0,
          isActive: true,
          trialDays: 3
        },
        gold: { 
          name: 'Gold', 
          price: 500, 
          features: ['ดูรูปไม่จำกัด', 'จองได้ 20 ครั้ง/เดือน'], 
          color: 'bg-yellow-100 text-yellow-800',
          maxImages: 0,
          dailyViews: -1,
          monthlyBookings: 20,
          revenueShare: 0,
          isActive: true,
          trialDays: 0
        },
        platinum: { 
          name: 'Platinum', 
          price: 1000, 
          features: ['ดูรูปไม่จำกัด', 'จองได้ไม่จำกัด', 'สิทธิพิเศษ'], 
          color: 'bg-purple-100 text-purple-800',
          maxImages: 0,
          dailyViews: -1,
          monthlyBookings: -1,
          revenueShare: 0,
          isActive: true,
          trialDays: 0
        }
      },
      girl: {
        free: { 
          name: 'Free', 
          price: 0, 
          features: ['อัพโหลดรูป 1 รูป'], 
          color: 'bg-gray-100 text-gray-800',
          maxImages: 1,
          dailyViews: 0,
          monthlyBookings: 0,
          revenueShare: 50,
          isActive: true,
          trialDays: 0
        },
        basic: { 
          name: 'Basic', 
          price: 100, 
          features: ['อัพโหลดรูป 3 รูป', 'รายได้เพิ่ม 10%'], 
          color: 'bg-blue-100 text-blue-800',
          maxImages: 3,
          dailyViews: 0,
          monthlyBookings: 0,
          revenueShare: 60,
          isActive: true,
          trialDays: 7
        },
        premium: { 
          name: 'Premium', 
          price: 300, 
          features: ['อัพโหลดรูป 6 รูป', 'รายได้เพิ่ม 20%'], 
          color: 'bg-green-100 text-green-800',
          maxImages: 6,
          dailyViews: 0,
          monthlyBookings: 0,
          revenueShare: 70,
          isActive: true,
          trialDays: 3
        },
        vip: { 
          name: 'VIP', 
          price: 500, 
          features: ['อัพโหลดรูปไม่จำกัด', 'รายได้เพิ่ม 30%', 'สิทธิพิเศษ'], 
          color: 'bg-purple-100 text-purple-800',
          maxImages: -1,
          dailyViews: 0,
          monthlyBookings: 0,
          revenueShare: 80,
          isActive: true,
          trialDays: 0
        }
      },
      momo: {
        starter: { 
          name: 'Starter', 
          price: 200, 
          features: ['จัดการทีม 3 คน', 'รายได้ 5%'], 
          color: 'bg-gray-100 text-gray-800',
          maxImages: 0,
          dailyViews: 0,
          monthlyBookings: 0,
          revenueShare: 5,
          isActive: true,
          trialDays: 7
        },
        basic: { 
          name: 'Basic', 
          price: 500, 
          features: ['จัดการทีม 10 คน', 'รายได้ 10%'], 
          color: 'bg-blue-100 text-blue-800',
          maxImages: 0,
          dailyViews: 0,
          monthlyBookings: 0,
          revenueShare: 10,
          isActive: true,
          trialDays: 3
        },
        pro: { 
          name: 'Pro', 
          price: 1000, 
          features: ['จัดการทีม 25 คน', 'รายได้ 15%'], 
          color: 'bg-green-100 text-green-800',
          maxImages: 0,
          dailyViews: 0,
          monthlyBookings: 0,
          revenueShare: 15,
          isActive: true,
          trialDays: 0
        },
        enterprise: { 
          name: 'Enterprise', 
          price: 2000, 
          features: ['จัดการทีม 50 คน', 'รายได้ 20%'], 
          color: 'bg-purple-100 text-purple-800',
          maxImages: 0,
          dailyViews: 0,
          monthlyBookings: 0,
          revenueShare: 20,
          isActive: true,
          trialDays: 0
        },
        ultra: { 
          name: 'Ultra Enterprise', 
          price: 5000, 
          features: ['จัดการทีมไม่จำกัด', 'รายได้ 25%', 'สิทธิพิเศษ'], 
          color: 'bg-red-100 text-red-800',
          maxImages: 0,
          dailyViews: 0,
          monthlyBookings: 0,
          revenueShare: 25,
          isActive: true,
          trialDays: 0
        }
      }
    };

    const configsRef = collection(db, 'tierConfigurations');
    
    for (const [role, tiers] of Object.entries(defaultConfigs)) {
      for (const [tierKey, config] of Object.entries(tiers)) {
        await addDoc(configsRef, {
          role,
          tierKey,
          ...config,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
      }
    }
    
    console.log('Default tier configurations initialized successfully');
  } catch (error) {
    console.error('Error initializing default tier configurations:', error);
    throw error;
  }
};

/**
 * ตรวจสอบว่า tier configuration มีอยู่หรือไม่
 * @param {string} role - role ของผู้ใช้
 * @param {string} tierKey - key ของ tier
 * @returns {Promise<boolean>} true ถ้ามี tier configuration
 */
export const tierConfigurationExists = async (role, tierKey) => {
  try {
    const config = await fetchTierConfiguration(role, tierKey);
    return config !== null;
  } catch (error) {
    console.error('Error checking tier configuration existence:', error);
    return false;
  }
};

/**
 * ดึงข้อมูล tier configurations ที่ใช้งานได้สำหรับ role ที่กำหนด
 * @param {string} role - role ของผู้ใช้
 * @returns {Promise<Object>} tier configurations ที่ใช้งานได้
 */
export const getActiveTierConfigurations = async (role) => {
  try {
    const configsRef = collection(db, 'tierConfigurations');
    const q = query(
      configsRef, 
      where('role', '==', role),
      where('isActive', '==', true),
      orderBy('price', 'asc')
    );
    const snapshot = await getDocs(q);
    
    const configs = {};
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      configs[data.tierKey] = { id: doc.id, ...data };
    });
    
    return configs;
  } catch (error) {
    console.error('Error getting active tier configurations:', error);
    throw error;
  }
};

/**
 * เพิ่มการใช้งาน promotion
 * @param {string} promotionId - ID ของ promotion
 * @returns {Promise<void>}
 */
export const incrementPromotionUsage = async (promotionId) => {
  try {
    const promotionRef = doc(db, 'promotions', promotionId);
    await updateDoc(promotionRef, {
      currentUses: increment(1),
      lastUsedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error incrementing promotion usage:', error);
    throw error;
  }
};

/**
 * ตรวจสอบว่า promotion สามารถใช้งานได้หรือไม่
 * @param {Object} promotion - ข้อมูล promotion
 * @returns {boolean} true ถ้า promotion สามารถใช้งานได้
 */
export const canUsePromotion = (promotion) => {
  if (!promotion.isActive) return false;
  
  const now = new Date();
  const startDate = new Date(promotion.startDate);
  const endDate = new Date(promotion.endDate);
  
  // ตรวจสอบวันที่
  if (now < startDate || now > endDate) return false;
  
  // ตรวจสอบจำนวนการใช้งาน
  if (promotion.maxUses > 0 && (promotion.currentUses || 0) >= promotion.maxUses) {
    return false;
  }
  
  return true;
};
